import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Watch, Gem, Feather, Footprints, Shirt, ScrollText, Landmark, Scale, HeartHandshake, BadgeCheck, ShieldCheck, Building2, FileSearch, VenetianMask, Slice, FlaskConical } from "lucide-react";
import { WHY_APPRAISAL, AUTH_SERVICES, BRAND_PROTECTION, CATEGORIES, CREDIBILITY, HERO_IMAGE, FASHION_DETECTIVE_URL } from "@/data/siteData";

const EASE = [0.2, 0.8, 0.2, 1];

export const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const SectionHeading = ({ label, title, sub }) => (
  <FadeIn>
    <p className="laa-label">{label}</p>
    <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl leading-[1.1] tracking-[-0.01em] text-[color:var(--laa-ivory)]">
      {title}
    </h2>
    {sub && (
      <p className="mt-4 max-w-2xl text-sm sm:text-base leading-relaxed text-[rgba(244,239,230,0.65)]">{sub}</p>
    )}
  </FadeIn>
);

/* ---------------- HERO ---------------- */

export const Hero = () => (
  <section id="top" data-testid="hero" className="relative noise overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_75%_15%,rgba(184,154,99,0.09),transparent_60%)]" />
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16 sm:pt-40 sm:pb-20 lg:pb-24 relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end">
        <div className="lg:col-span-7">
          <motion.p
            className="laa-label"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            Est. 2019 · The Authority in Luxury Resale
          </motion.p>
          <motion.h1
            className="mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-[-0.02em] text-[color:var(--laa-ivory)]"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.08, ease: EASE }}
          >
            Appraisals & authentication for the world’s finest goods.
          </motion.h1>
          <motion.p
            className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-[rgba(244,239,230,0.7)]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18, ease: EASE }}
          >
            USPAP-compliant appraisals, expert authentication, and counterfeit detection —
            trusted by federal courts, government agencies, and the largest resale platforms.
          </motion.p>
          <motion.div
            className="mt-9 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.28, ease: EASE }}
          >
            <a href={FASHION_DETECTIVE_URL} target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="hero-primary-cta"
                className="rounded-xl px-6 py-5 text-sm font-medium bg-[color:var(--laa-ivory)] text-[color:var(--laa-ink)] hover:bg-white focus-visible:ring-2 focus-visible:ring-[color:var(--laa-champagne)]"
              >
                Request an Appraisal
              </Button>
            </a>
            <a href="#contact">
              <Button
                data-testid="hero-secondary-cta"
                variant="outline"
                className="rounded-xl px-6 py-5 text-sm font-medium bg-transparent border-[color:var(--laa-hairline)] text-[color:var(--laa-ivory)] hover:bg-[rgba(244,239,230,0.05)] hover:text-[color:var(--laa-ivory)] hover:border-[rgba(244,239,230,0.3)]"
              >
                Contact Us
              </Button>
            </a>
          </motion.div>
          <motion.div
            className="mt-10 flex flex-wrap gap-x-6 gap-y-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4, ease: EASE }}
          >
            {[
              { icon: BadgeCheck, text: "AAA Certified" },
              { icon: ShieldCheck, text: "USPAP Compliant" },
              { icon: Scale, text: "Trial Expert Witness" },
            ].map(({ icon: Icon, text }) => (
              <span key={text} className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-[rgba(244,239,230,0.55)]">
                <Icon size={14} className="text-[color:var(--laa-champagne)]" />
                {text}
              </span>
            ))}
          </motion.div>
        </div>
        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
        >
          <div className="relative noise rounded-2xl overflow-hidden border border-[color:var(--laa-hairline)]">
            <img
              src={HERO_IMAGE}
              alt="Cartier ring hallmark examined under a jeweler's loupe"
              className="w-full h-[320px] sm:h-[420px] object-cover object-[50%_30%]"
              loading="eager"
            />
            <div className="absolute bottom-0 inset-x-0 p-4 bg-[linear-gradient(to_top,rgba(14,15,16,0.85),transparent)]">
              <p className="text-[10px] tracking-[0.24em] uppercase text-[rgba(244,239,230,0.8)]">
                Lot No. 001 — Connoisseurship, Verified
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ---------------- CREDIBILITY STRIP ---------------- */

export const CredibilityStrip = () => (
  <FadeIn>
    <section data-testid="credibility-strip" className="border-y border-[color:var(--laa-hairline)] bg-[color:var(--laa-ink-2)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
        {CREDIBILITY.map((c) => (
          <div key={c.label} className="text-center sm:text-left">
            <p className="font-serif-display text-2xl sm:text-3xl text-[color:var(--laa-ivory)]">{c.value}</p>
            <p className="mt-1 text-[11px] tracking-[0.16em] uppercase text-[rgba(244,239,230,0.5)]">{c.label}</p>
          </div>
        ))}
      </div>
    </section>
  </FadeIn>
);

/* ---------------- WHY APPRAISAL ---------------- */

const WHY_ICONS = { Insurance: ScrollText, Estate: Landmark, Divorce: Scale, Donation: HeartHandshake };

export const WhyAppraisal = () => (
  <section id="why" data-testid="why-appraisal-section" className="py-16 sm:py-20 lg:py-24">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading
        label="Why You Need an Appraisal"
        title="Documentation is the difference between an heirloom and a guess."
        sub="A professional appraisal establishes the existence, condition, and fair value of your property — accepted by insurers, courts, and the IRS."
      />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {WHY_APPRAISAL.map((item, i) => {
          const Icon = WHY_ICONS[item.label];
          return (
            <FadeIn key={item.label} delay={i * 0.07}>
              <div
                data-testid={`why-card-${item.label.toLowerCase()}`}
                className="laa-card h-full rounded-2xl border border-[color:var(--laa-hairline)] bg-[rgba(20,21,23,0.55)] p-7"
              >
                <Icon size={20} className="text-[color:var(--laa-champagne)]" />
                <p className="laa-label mt-5">{item.label}</p>
                <h3 className="mt-2 text-xl leading-[1.15] text-[color:var(--laa-ivory)]">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[rgba(244,239,230,0.62)]">{item.copy}</p>
              </div>
            </FadeIn>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- AUTHENTICATION SERVICES ---------------- */

const SVC_ICONS = { "When Selling": BadgeCheck, "When Buying": ShieldCheck, "Business Services": Building2 };

export const AuthServices = () => (
  <section id="services" data-testid="auth-services-section" className="py-16 sm:py-20 lg:py-24 border-t border-[color:var(--laa-hairline)]">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <SectionHeading
            label="Authentication Services"
            title="A $500 billion market runs on trust."
            sub="The pre-owned luxury industry is projected to reach $500B by 2030. Protect your investments with proper verification — before and after every sale."
          />
          <FadeIn delay={0.15}>
            <a
              href={FASHION_DETECTIVE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-fashion-detective-link"
              className="mt-8 inline-flex items-center gap-2 laa-link text-sm text-[color:var(--laa-champagne)]"
            >
              Authenticate your items now at TheFashionDetective.com →
            </a>
          </FadeIn>
        </div>
        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {AUTH_SERVICES.map((s, i) => {
            const Icon = SVC_ICONS[s.label];
            return (
              <FadeIn key={s.label} delay={i * 0.08} className="h-full">
                <div
                  data-testid={`service-card-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="laa-card h-full rounded-2xl border border-[color:var(--laa-hairline)] bg-[rgba(20,21,23,0.55)] p-7 flex flex-col"
                >
                  <Icon size={20} className="text-[color:var(--laa-champagne)]" />
                  <p className="laa-label mt-5">{s.label}</p>
                  <h3 className="mt-2 text-xl leading-[1.15] text-[color:var(--laa-ivory)]">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[rgba(244,239,230,0.62)]">{s.copy}</p>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- BRAND PROTECTION ---------------- */

const BP_ICONS = {
  Investigations: FileSearch,
  "Secret Shopping": VenetianMask,
  Dissection: Slice,
  "Scientific Testing": FlaskConical,
};

export const BrandProtection = () => (
  <section id="protection" data-testid="brand-protection-section" className="py-16 sm:py-20 lg:py-24 border-t border-[color:var(--laa-hairline)] bg-[color:var(--laa-ink-2)]">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading
        label="Brand Protection Services"
        title="When authenticity is contested, evidence decides."
        sub="For brands, legal teams, and platforms defending their intellectual property — from discreet field work to laboratory analysis, every finding is documented to trial standard."
      />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {BRAND_PROTECTION.map((item, i) => {
          const Icon = BP_ICONS[item.label];
          return (
            <FadeIn key={item.label} delay={i * 0.07} className="h-full">
              <div
                data-testid={`protection-card-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="laa-card h-full rounded-2xl border border-[color:var(--laa-hairline)] bg-[rgba(20,21,23,0.55)] p-7"
              >
                <Icon size={20} className="text-[color:var(--laa-champagne)]" />
                <p className="laa-label mt-5">{item.label}</p>
                <h3 className="mt-2 text-xl leading-[1.15] text-[color:var(--laa-ivory)]">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[rgba(244,239,230,0.62)]">{item.copy}</p>
              </div>
            </FadeIn>
          );
        })}
      </div>
      <FadeIn delay={0.2}>
        <p className="mt-10 text-sm text-[rgba(244,239,230,0.5)]">
          Retained for a matter or building a case?{" "}
          <a href="#contact" data-testid="protection-contact-link" className="laa-link text-[color:var(--laa-champagne)]">
            Contact us in confidence.
          </a>
        </p>
      </FadeIn>
    </div>
  </section>
);

/* ---------------- CATEGORIES ---------------- */

const CAT_ICONS = { ShoppingBag, Watch, Gem, Feather, Footprints, Shirt };

export const Categories = ({ onSelectCategory }) => (
  <section id="categories" data-testid="categories-section" className="py-16 sm:py-20 lg:py-24 border-t border-[color:var(--laa-hairline)]">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading
        label="Luxury Categories"
        title="Every category, examined with connoisseurship."
        sub="Select a category to enquire about your item. Something else? Contact us below."
      />
      <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        {CATEGORIES.map((cat, i) => {
          const Icon = CAT_ICONS[cat.icon];
          return (
            <FadeIn key={cat.name} delay={i * 0.05} className="h-full">
              <button
                type="button"
                data-testid={`category-card-${cat.short.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => onSelectCategory(cat.name)}
                className="laa-card group w-full h-full text-left rounded-2xl border border-[color:var(--laa-hairline)] bg-[rgba(20,21,23,0.55)] p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--laa-champagne)]"
              >
                <Icon size={22} className="text-[color:var(--laa-champagne)]" />
                <p className="mt-4 font-serif-display text-base text-[color:var(--laa-ivory)]">{cat.short}</p>
                <p className="mt-2 text-[11px] leading-relaxed text-[rgba(244,239,230,0.45)] group-hover:text-[rgba(244,239,230,0.65)]" style={{ transition: "color 200ms" }}>
                  {cat.blurb}
                </p>
              </button>
            </FadeIn>
          );
        })}
      </div>
    </div>
  </section>
);
